import { Link } from 'react-router'
import { AngledBand } from '@carouselgg/nexus/angled-band'
import { DisplayHeading } from '@carouselgg/nexus/display-heading'
import { GradientText } from '@carouselgg/nexus/gradient-text'
import { ScrollReveal } from '@carouselgg/nexus/scroll-reveal'
import { RadarChart } from '@carouselgg/nexus/radar-chart'
import { TrustGauge } from '@carouselgg/nexus/trust-gauge'
import { ShieldCheck, ShieldAlert, ArrowRight } from '@carouselgg/nexus/icon'
import { useTrackVisibility } from '../hooks/useTrackVisibility'
import { demoApplicants } from './demoData'
import styles from './Scoring.module.css'

/**
 * Ladder mirrors api/internal/auth/trust_score.go `levelFromScore`.
 * Keep these ranges in step with it.
 */
const LEVELS = [
  { name: 'Unverified', range: '0', tone: 'var(--nexus-text-muted)' },
  { name: 'Basic', range: '1–19', tone: 'var(--nexus-text-secondary)' },
  { name: 'Verified', range: '20–49', tone: 'var(--nexus-blue)' },
  { name: 'Established', range: '50–79', tone: 'var(--nexus-green)' },
  { name: 'Veteran', range: '80–100', tone: 'var(--nexus-purple)' },
]

const TONE = {
  good: { icon: ShieldCheck, color: 'var(--nexus-green)', className: styles.good },
  bad: { icon: ShieldAlert, color: 'var(--nexus-pink)', className: styles.bad },
}

export function Scoring() {
  const ref = useTrackVisibility<HTMLDivElement>('scoring')

  return (
    <AngledBand id="scoring" tone="elevated" slice="both">
      <div className={styles.inner} ref={ref}>
        <ScrollReveal>
          <div className={styles.head}>
            <div>
              <p className={styles.eyebrow}>
                <span className={styles.seed}>03</span>
                <span className={styles.rule} />
                Trust Score
              </p>
              <DisplayHeading size="6xl">
                One number.{' '}
                <br />
                <GradientText gradient="ocean" angle={115}>No hiding.</GradientText>
              </DisplayHeading>
            </div>
            <div>
              <p className={styles.lede}>
                Every player gets a Trust Score from 0–100, built from verified identity, real
                competitive history, and how they actually play. Smurf behaviour drags it down, and
                the league sees exactly which signals moved it.
              </p>
              <Link to="/how-it-works" className={styles.headLink}>
                See how scoring works
                <ArrowRight size={15} />
              </Link>
            </div>
          </div>
        </ScrollReveal>

        <div className={styles.compare}>
          {demoApplicants.map((applicant, index) => {
            const tone = TONE[applicant.tone]
            const Icon = tone.icon
            return (
              <ScrollReveal key={applicant.id} delay={index * 90}>
                <div className={styles.card}>
                  <div className={styles.cardHead}>
                    <h3 className={styles.cardTitle}>
                      <Icon size={19} className={tone.className} />
                      <span>
                        {applicant.handle}
                        <span className={styles.level}>{applicant.level}</span>
                      </span>
                    </h3>
                    <TrustGauge
                      score={applicant.score}
                      size={62}
                      gradient={{ from: tone.color, to: tone.color }}
                      title={`Trust Score ${applicant.score}`}
                    />
                  </div>

                  <div className={styles.chart}>
                    <RadarChart
                      data={applicant.radar}
                      height={260}
                      color={tone.color}
                      name={applicant.handle}
                      baselineName="Bracket median"
                      showLegend={false}
                      // Pinned, not auto-scaled. Left to itself each chart
                      // normalises to its own maximum, so a player sitting on
                      // the median and a four-sigma outlier both fill the
                      // hexagon and the side-by-side comparison says nothing.
                      maxValue={100}
                    />
                  </div>

                  <p className={styles.caption}>{applicant.evidence}</p>
                </div>
              </ScrollReveal>
            )
          })}
        </div>

        <ScrollReveal>
          <div className={styles.levels}>
            <span className={styles.levelsLabel}>Levels</span>
            <div className={styles.levelsGrid}>
              {LEVELS.map((level) => (
                <div key={level.name} className={styles.levelCell}>
                  <span
                    className={styles.levelName}
                    style={{ ['--level-tone' as string]: level.tone }}
                  >
                    {level.name}
                  </span>
                  <span className={styles.levelRange}>{level.range}</span>
                </div>
              ))}
            </div>
          </div>
        </ScrollReveal>
      </div>
    </AngledBand>
  )
}
